import api from "api/axios";

export type SheetColumnType = "text" | "number" | "date" | "boolean" | "url" | "select";
export type SharePermission = "view" | "edit";

export interface SheetCategory {
  id: number;
  name: string;
  description: string | null;
  is_active: boolean;
  created_at: string | null;
  updated_at: string | null;
}

export interface Sheet {
  id: number;
  name: string;
  description: string | null;
  category_id: number | null;
  tags: string[];
  is_active: boolean;
  auto_save_enabled: boolean;
  is_readonly: boolean;
  created_at: string | null;
  updated_at: string | null;
  /** Only present on the employee-router response — the requesting employee's
   * own share permission for this sheet. Admin has no share row, so treat
   * undefined as full access when the caller is an admin. */
  my_permission?: SharePermission;
}

// Note: no is_active field — the backend only ever returns active columns
// (list_columns filters at the query level, and SheetColumnResponse has no
// is_active field), so there's nothing here to filter on client-side.
export interface SheetColumn {
  id: number;
  sheet_id: number;
  key: string;
  label: string;
  type: SheetColumnType;
  select_options: string[] | null;
  position: number;
  width: number | null;
}

// Note: no is_active field — same reasoning as SheetColumn above
// (SheetRowResponse doesn't include one either).
export interface SheetRow {
  id: number;
  sheet_id: number;
  data: Record<string, unknown>;
  position: number;
  /** null = auto height (grows to fit content); set once a user drags to resize. */
  height: number | null;
  version: number;
  created_at: string | null;
  updated_at: string | null;
}

export interface SheetShare {
  id: number;
  sheet_id: number;
  employee_id: number;
  employee_code: string;
  employee_name: string;
  permission: SharePermission;
  created_at: string | null;
}

export interface SheetCheckpoint {
  id: number;
  sheet_id: number;
  label: string | null;
  column_count: number;
  row_count: number;
  created_at: string | null;
}

export interface AssignableEmployee {
  id: number;
  employee_code: string;
  name: string;
}

export interface SimilarMatch {
  id: number;
  name: string;
  category_id: number | null;
  ratio: number;
}

export interface ListResponse<T> {
  items: T[];
  total_count: number;
  total_pages: number;
  page: number;
}

export interface SheetFilters {
  search?: string;
  category_id?: number;
  tag?: string;
  page?: number;
  page_size?: number;
}

export interface CsvImportPreview {
  matched_headers: string[];
  /** Headers with no existing column match — created as new text columns on commit. */
  new_columns: string[];
  /** Untouched default A/B/C... columns dropped because they had no data and no header matched them. */
  removed_columns: string[];
  /** Headers with no existing column match that WON'T be created — only populated
   * for a non-admin importer, since column creation/replacement is admin-only. */
  skipped_headers: string[];
  row_count: number;
}

function sheetsApiBase(): string {
  return localStorage.getItem("authUserType") === "admin" ? "/admin/sheets" : "/employee/sheets";
}

function isAdminBase(): boolean {
  return sheetsApiBase() === "/admin/sheets";
}

// ── Sheets ───────────────────────────────────────────────────────────────

export async function listSheets(filters: SheetFilters = {}): Promise<ListResponse<Sheet>> {
  const params = new URLSearchParams();
  if (filters.search) params.set("search", filters.search);
  if (filters.category_id !== undefined) params.set("category_id", String(filters.category_id));
  if (filters.tag) params.set("tag", filters.tag);
  params.set("page", String(filters.page ?? 1));
  params.set("page_size", String(filters.page_size ?? 20));
  const res = await api.get(`${sheetsApiBase()}?${params.toString()}`);
  return res.data;
}

export async function getSheet(id: number): Promise<Sheet> {
  const res = await api.get(`${sheetsApiBase()}/${id}`);
  return res.data;
}

export async function createSheet(data: {
  name: string;
  description?: string;
  category_id?: number | null;
  tags?: string[];
}): Promise<Sheet> {
  const res = await api.post(`/admin/sheets`, data);
  return res.data;
}

export async function updateSheet(
  id: number,
  data: { name?: string; description?: string; category_id?: number | null; tags?: string[] }
): Promise<Sheet> {
  const res = await api.patch(`/admin/sheets/${id}`, data);
  return res.data;
}

export async function deleteSheet(id: number): Promise<void> {
  await api.delete(`/admin/sheets/${id}`);
}

export async function updateAutoSave(sheetId: number, enabled: boolean): Promise<Sheet> {
  const res = await api.patch(`${sheetsApiBase()}/${sheetId}/auto-save`, { enabled });
  return res.data;
}

// Admin-only, unlike updateAutoSave above — mirrors updateSheet/createSheet's
// hardcoded /admin/sheets path rather than sheetsApiBase().
export async function updateReadonly(sheetId: number, enabled: boolean): Promise<Sheet> {
  const res = await api.patch(`/admin/sheets/${sheetId}/readonly`, { enabled });
  return res.data;
}

export async function checkSimilarSheets(name: string): Promise<SimilarMatch[]> {
  if (!name.trim()) return [];
  const res = await api.get(`/admin/sheets/similar`, { params: { name } });
  return res.data.matches ?? [];
}

// ── Categories ───────────────────────────────────────────────────────────

export async function listCategories(): Promise<SheetCategory[]> {
  const res = await api.get(`${sheetsApiBase()}/categories`);
  return res.data;
}

export async function createCategory(data: { name: string; description?: string }): Promise<SheetCategory> {
  const res = await api.post(`/admin/sheets/categories`, data);
  return res.data;
}

export async function updateCategory(
  id: number,
  data: { name?: string; description?: string }
): Promise<SheetCategory> {
  const res = await api.patch(`/admin/sheets/categories/${id}`, data);
  return res.data;
}

export async function deleteCategory(id: number): Promise<void> {
  await api.delete(`/admin/sheets/categories/${id}`);
}

export async function checkSimilarCategories(name: string): Promise<SimilarMatch[]> {
  if (!name.trim()) return [];
  const res = await api.get(`/admin/sheets/categories/similar`, { params: { name } });
  return res.data.matches ?? [];
}

// ── Columns (admin only) ─────────────────────────────────────────────────

export async function listColumns(sheetId: number): Promise<SheetColumn[]> {
  const res = await api.get(`${sheetsApiBase()}/${sheetId}/columns`);
  return res.data;
}

export async function createColumn(
  sheetId: number,
  data: { key: string; label: string; type: SheetColumnType; select_options?: string[]; width?: number }
): Promise<SheetColumn> {
  const res = await api.post(`/admin/sheets/${sheetId}/columns`, data);
  return res.data;
}

export async function updateColumn(
  sheetId: number,
  columnId: number,
  data: { label?: string; type?: SheetColumnType; select_options?: string[]; width?: number; position?: number }
): Promise<SheetColumn> {
  const res = await api.patch(`/admin/sheets/${sheetId}/columns/${columnId}`, data);
  return res.data;
}

export async function deleteColumn(sheetId: number, columnId: number): Promise<void> {
  await api.delete(`/admin/sheets/${sheetId}/columns/${columnId}`);
}

export async function reorderColumns(sheetId: number, columnIds: number[]): Promise<SheetColumn[]> {
  const res = await api.patch(`/admin/sheets/${sheetId}/columns/reorder`, { column_ids: columnIds });
  return res.data;
}

// ── Rows ─────────────────────────────────────────────────────────────────

export async function listRows(sheetId: number, page = 1, pageSize = 100): Promise<ListResponse<SheetRow>> {
  const res = await api.get(`${sheetsApiBase()}/${sheetId}/rows`, { params: { page, page_size: pageSize } });
  return res.data;
}

export async function createRow(sheetId: number, data: Record<string, unknown>): Promise<SheetRow> {
  const res = await api.post(`${sheetsApiBase()}/${sheetId}/rows`, { data });
  return res.data;
}

export async function bulkCreateRows(sheetId: number, rows: Record<string, unknown>[]): Promise<SheetRow[]> {
  const res = await api.post(`${sheetsApiBase()}/${sheetId}/rows/bulk`, { rows });
  return res.data;
}

export class RowConflictError extends Error {
  currentRow: SheetRow;
  constructor(currentRow: SheetRow) {
    super("This row changed since it was loaded.");
    this.currentRow = currentRow;
  }
}

export async function updateRow(
  sheetId: number,
  rowId: number,
  data: Record<string, unknown>,
  version: number
): Promise<SheetRow> {
  try {
    const res = await api.patch(`${sheetsApiBase()}/${sheetId}/rows/${rowId}`, { data, version });
    return res.data;
  } catch (err: any) {
    if (err?.response?.status === 409) {
      // Backend's 409 body is { detail, current_row: {...} } — the row
      // itself is nested, not the top-level payload.
      throw new RowConflictError(err.response.data?.current_row);
    }
    throw err;
  }
}

export async function deleteRow(sheetId: number, rowId: number): Promise<void> {
  await api.delete(`${sheetsApiBase()}/${sheetId}/rows/${rowId}`);
}

export async function restoreRow(sheetId: number, rowId: number): Promise<SheetRow> {
  const res = await api.post(`${sheetsApiBase()}/${sheetId}/rows/${rowId}/restore`);
  return res.data;
}

export async function reorderRows(sheetId: number, rowIds: number[]): Promise<SheetRow[]> {
  const res = await api.patch(`${sheetsApiBase()}/${sheetId}/rows/reorder`, { row_ids: rowIds });
  return res.data;
}

export async function updateRowHeight(sheetId: number, rowId: number, height: number | null): Promise<SheetRow> {
  const res = await api.patch(`${sheetsApiBase()}/${sheetId}/rows/${rowId}/height`, { height });
  return res.data;
}

// ── Checkpoints (manual version history, admin only) ───────────────────────

export async function createCheckpoint(sheetId: number, label: string | null): Promise<SheetCheckpoint> {
  const res = await api.post(`/admin/sheets/${sheetId}/checkpoints`, { label });
  return res.data;
}

export async function listCheckpoints(sheetId: number): Promise<SheetCheckpoint[]> {
  const res = await api.get(`/admin/sheets/${sheetId}/checkpoints`);
  return res.data;
}

export async function deleteCheckpoint(sheetId: number, checkpointId: number): Promise<void> {
  await api.delete(`/admin/sheets/${sheetId}/checkpoints/${checkpointId}`);
}

export async function purgeCheckpointsOlderThan(sheetId: number, olderThanDays: number): Promise<{ deleted: number }> {
  const res = await api.delete(`/admin/sheets/${sheetId}/checkpoints/purge`, { params: { older_than_days: olderThanDays } });
  return res.data;
}

export async function restoreCheckpoint(sheetId: number, checkpointId: number): Promise<SheetCheckpoint> {
  const res = await api.post(`/admin/sheets/${sheetId}/checkpoints/${checkpointId}/restore`);
  return res.data;
}

// ── CSV import/export ───────────────────────────────────────────────────

export async function importCsv(sheetId: number, file: File, dryRun: boolean): Promise<CsvImportPreview> {
  const form = new FormData();
  form.append("file", file);
  const res = await api.post(`${sheetsApiBase()}/${sheetId}/import-csv`, form, {
    params: { dry_run: dryRun },
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
}

export async function exportXlsx(sheetId: number): Promise<Blob> {
  const res = await api.get(`${sheetsApiBase()}/${sheetId}/export-xlsx`, { responseType: "blob" });
  return res.data;
}

// ── Sharing (admin only) ─────────────────────────────────────────────────

export async function listShares(sheetId: number): Promise<SheetShare[]> {
  const res = await api.get(`/admin/sheets/${sheetId}/shares`);
  return res.data;
}

export async function shareSheet(
  sheetId: number,
  employeeId: number,
  permission: SharePermission
): Promise<SheetShare> {
  const res = await api.post(`/admin/sheets/${sheetId}/shares`, { employee_id: employeeId, permission });
  return res.data;
}

export async function updateSharePermission(
  sheetId: number,
  shareId: number,
  permission: SharePermission
): Promise<SheetShare> {
  const res = await api.patch(`/admin/sheets/${sheetId}/shares/${shareId}`, { permission });
  return res.data;
}

export async function unshareSheet(sheetId: number, shareId: number): Promise<void> {
  await api.delete(`/admin/sheets/${sheetId}/shares/${shareId}`);
}

export async function listAssignableEmployees(): Promise<AssignableEmployee[]> {
  const res = await api.get(`/admin/sheets/assignable-employees`);
  return res.data;
}

export { isAdminBase };
